import { ILogItem } from "./log-item";


/**
 * Which route was used to ask OpenAI for the image.
 *
 * 'batch'        - the OpenAI Batch API. ~50% cheaper, but the only completion
 *                  window on offer is 24 hours.
 * 'synchronous'  - a plain images.generate call. Full price, results in minutes.
 *
 * Selected globally (IArtworkGenerationSettings.mode) but stamped onto the batch
 * when it is submitted, so a batch already in flight keeps being followed up on
 * the route it was actually dispatched on even if the global setting changes.
 */
export type ArtworkGenerationMode = 'batch' | 'synchronous';

export type ArtworkGenerationModel = 'gpt-image-2' | 'gpt-image-1-mini';

export type ArtworkGenerationQuality = 'low' | 'medium' | 'high' | 'auto';

export type ArtworkOrientation = 'portrait' | 'landscape' | 'square';

export type ArtworkGenerationBatchStatus =
    'draft' | 'pending' | 'processing' | 'completed' | 'creating_posters' | 'failed';

export type ArtworkToGenerateStatus =
    'pending' | 'generating' | 'completed' | 'approved' | 'rejected' | 'failed';


export interface IGeneratedArtwork {
    identifier: string,
    name: string,
    url: string,
    contentType: string,
    width: number,
    height: number,
    size: number,
    createdPosterIdByType: {
        poster?: string,
        canvas?: string
    }
}

export interface IArtworkToGenerate {
    identifier: string,
    name: string,
    generatedArtwork?: IGeneratedArtwork,
    status: ArtworkToGenerateStatus,
    error?: string,
    orientation: ArtworkOrientation,
    /**
     * The print ratio the prompt is written for. 'square' orientation overrides
     * this to 1:1 in the size resolver - see ARTWORK_FORMATS.
     */
    aspectRatio: '5:7',
    stylePrompt: string,
    motifPrompt: string,
    fullPrompt: string,
    /** The route this artwork was actually dispatched on. */
    generationMode?: ArtworkGenerationMode,
    /** Batch mode only: the OpenAI batch this artwork was submitted in. */
    openAiBatchId?: string,
    /** Batch mode only: the custom_id used in the JSONL, maps an output line back here. */
    customId?: string,
    submitted?: Date,
    completed?: Date,
    /** Retry guard. An artwork is abandoned once this reaches MAX_ARTWORK_ATTEMPTS. */
    attempts?: number,
}

/**
 * Shopify publishing options for a batch. Only offered when the store is on
 * Shopify and the batch creates posters and/or canvas, since a Shopify product
 * is created from a poster/canvas product rather than from the artwork itself.
 */
export interface IArtworkGenerationShopifySettings {
    createProduct: boolean,
    /** Size variants to publish on the generated POSTER products. */
    posterSizes: string[],
    /** Size variants to publish on the generated CANVAS products. */
    canvasSizes: string[],
    /** Tags applied to every Shopify product created from this batch. */
    tags: string[],
    /**
     * Shopify product taxonomy category per product type, from the `taxonomy`
     * query. The name is kept alongside the id so the UI can show the current
     * choice without calling Shopify again.
     */
    posterCategory?: IArtworkGenerationCategory,
    canvasCategory?: IArtworkGenerationCategory,
    /**
     * Superseded by the two above. Still read when loading a batch created
     * before the split, so its choice is not silently lost.
     */
    category?: IArtworkGenerationCategory,
    /** Shopify product vendor. Carried forward from the last submitted batch. */
    vendor?: string,
    /**
     * Publish a second set of poster variants with a white passepartout border.
     * Poster products only - the border is a paper mount, not a canvas finish.
     */
    createPassepartoutVariants?: boolean,
    /** Variant price per size, in the store currency. */
    posterPrices?: { [size: string]: number },
    canvasPrices?: { [size: string]: number },
    /** Variant price per size for the passepartout variants. */
    passepartoutPrices?: { [size: string]: number },
    /**
     * Render the artwork into an interior photo and publish it as an extra
     * product image. Costs one image generation per created product.
     */
    createMockup?: boolean,
    /**
     * Describes the scene the artwork is placed in. Prefilled with
     * DEFAULT_MOCKUP_PROMPT and editable per batch.
     */
    mockupPrompt?: string,
    /**
     * Render canvas mockups from canvasMockupPrompt instead of mockupPrompt. A
     * canvas hangs unframed with visible edges, so the poster scene - which asks
     * for a slim frame - does not describe it well.
     */
    useCanvasMockupPrompt?: boolean,
    /**
     * The scene for canvas mockups when useCanvasMockupPrompt is on. Prefilled
     * with DEFAULT_CANVAS_MOCKUP_PROMPT.
     */
    canvasMockupPrompt?: string,
    /**
     * Publish the created product to the Online Store so buyers can see it.
     * Left off, the product is created ACTIVE but unpublished - Shopify's own
     * default - and stays invisible in the shop until published by hand.
     *
     * Which channel is not configurable: picking channels needs the shop's
     * publication ids, and those cannot be read without read_publications.
     */
    publishProduct?: boolean
}

/** A Shopify taxonomy category as stored on a batch. */
export interface IArtworkGenerationCategory {
    id: string,
    name: string
}

export interface IOpenAiImageBatch {
    id: string,
    status: string,
    customIds: string[],
    inputFileId?: string,
    outputFileId?: string,
    errorFileId?: string,
    created: Date,
    lastChecked?: Date
}

export interface IArtworkGenerationBatch {
    _id?: any,
    storeId: string,
    name: string,
    /** Stamped from the global setting at submit time. Never re-read from globals afterwards. */
    generationMode?: ArtworkGenerationMode,
    /** Chosen per batch, so models can be compared side by side. */
    model: ArtworkGenerationModel,
    quality: ArtworkGenerationQuality,
    /** Shared across every artwork in the batch, copied onto each one. */
    stylePrompt: string,
    orientation: ArtworkOrientation,
    aspectRatio: '5:7',
    /** Batch mode only. One entry per OpenAI batch submitted for this artwork batch. */
    openAiBatches?: IOpenAiImageBatch[],
    createPosters?: boolean,
    createCanvas?: boolean,
    /**
     * Re-queue rejected artworks for a fresh generation. Left on, every new
     * rejection is regenerated on the next run - each cycle still needs a
     * deliberate rejection, so it cannot run away on its own.
     */
    regenerateRejected?: boolean,
    shopify?: IArtworkGenerationShopifySettings,
    status: ArtworkGenerationBatchStatus,
    artworks: IArtworkToGenerate[],
    log: ILogItem[],
    created: Date,
    submitted?: Date,
    finished?: Date
}


/**
 * Pixel sizes must be divisible by 16 and stay between a 1:3 and 3:1 ratio for
 * gpt-image-2, so these are exact-ratio sizes that satisfy both constraints.
 */
export const ARTWORK_FORMATS: {
    [orientation in ArtworkOrientation]: {
        size: string,
        printSize: string,
        ratio: string,
        orientationLabel: string
    }
} = {
    portrait: { size: '1200x1680', printSize: '500 × 700 mm', ratio: '5:7', orientationLabel: 'Portrait' },
    landscape: { size: '1680x1200', printSize: '700 × 500 mm', ratio: '7:5', orientationLabel: 'Landscape' },
    square: { size: '1200x1200', printSize: '500 × 500 mm', ratio: '1:1', orientationLabel: 'Square' }
};

/**
 * The image models a batch can be generated with.
 *
 * `sizes` is what the model is asked for, when it cannot produce the exact
 * ARTWORK_FORMATS size. gpt-image-1-mini only offers fixed sizes, so it renders
 * 2:3 and the result is centre-cropped to the print ratio when it is ingested -
 * the prompt already keeps focal elements crop-safe.
 */
export const ARTWORK_GENERATION_MODELS: {
    [model in ArtworkGenerationModel]: {
        label: string,
        defaultQuality: ArtworkGenerationQuality,
        sizes?: { [orientation in ArtworkOrientation]: string }
    }
} = {
    'gpt-image-1-mini': {
        label: 'gpt-image-1-mini',
        defaultQuality: 'medium',
        sizes: { portrait: '1024x1536', landscape: '1536x1024', square: '1024x1024' }
    },
    'gpt-image-2': {
        label: 'gpt-image-2',
        defaultQuality: 'high'
    }
};

export const isArtworkGenerationModel = (value: any): value is ArtworkGenerationModel => {
    return typeof value == 'string' && ARTWORK_GENERATION_MODELS.hasOwnProperty(value);
};

/** The size to request from OpenAI for a model and orientation. */
export const artworkRequestSize = (model: ArtworkGenerationModel, orientation: ArtworkOrientation) => {
    let definition = ARTWORK_GENERATION_MODELS[model];
    return definition && definition.sizes ? definition.sizes[orientation] : ARTWORK_FORMATS[orientation].size;
};

/**
 * The scene a mockup is rendered in. Prefilled into the batch form and stored on
 * the batch, so an edit there never has to be repeated and a batch created
 * earlier keeps rendering the scene it was configured with.
 *
 * Written as a scene brief rather than a full-image brief: the artwork itself is
 * supplied as the input image, and the strongest instruction here is to
 * reproduce it untouched. Image models will happily "improve" a supplied artwork
 * otherwise, which would sell a picture the customer does not receive.
 */
export const DEFAULT_MOCKUP_PROMPT = `Place the supplied artwork as a framed poster hanging on the wall of a cozy, clean living room.

SCENE
- Bright, airy Scandinavian living room with a warm, calm and inviting atmosphere.
- Soft natural daylight from a window outside the frame. Gentle shadows, no harsh highlights.
- Neutral off-white wall. Tasteful and uncluttered styling: a light linen sofa with a few cushions, a small side table, a green plant in a simple pot, a soft rug.
- Muted natural palette - warm wood, linen and ceramics - that keeps the artwork the focal point.

THE ARTWORK
- Reproduce the supplied artwork EXACTLY: same composition, colors, crop and proportions. Do not redraw, restyle, extend, crop or recolor it.
- Hang it centred on the wall in a slim, minimal frame at natural eye level.
- Realistic perspective and scale for a large wall poster, with a subtle drop shadow.

OUTPUT
- Photorealistic interior photograph, shot straight on at eye level with a shallow depth of field.
- No text, no watermark, no people and no other wall art.`;

/**
 * The default scene for canvas mockups, used when a batch gives canvas its own
 * prompt. Same room as DEFAULT_MOCKUP_PROMPT, but the artwork is a stretched
 * canvas: no frame, with the wrapped edge visible.
 */
export const DEFAULT_CANVAS_MOCKUP_PROMPT = `Place the supplied artwork as a stretched canvas print hanging on the wall of a cozy, clean living room.

SCENE
- Bright, airy Scandinavian living room with a warm, calm and inviting atmosphere.
- Soft natural daylight from a window outside the frame. Gentle shadows, no harsh highlights.
- Neutral off-white wall. Tasteful and uncluttered styling: a light linen sofa with a few cushions, a small side table, a green plant in a simple pot, a soft rug.
- Muted natural palette - warm wood, linen and ceramics - that keeps the artwork the focal point.

THE ARTWORK
- Reproduce the supplied artwork EXACTLY: same composition, colors, crop and proportions. Do not redraw, restyle, extend, crop or recolor it.
- Show it as a gallery-wrapped canvas on a deep wooden stretcher frame: NO frame, NO glass, NO passepartout.
- The image wraps around the edges; the side of the canvas is subtly visible thanks to a slight angle, with a soft matte canvas texture.
- Hang it centred on the wall at natural eye level, with realistic perspective, scale and a soft shadow cast by the canvas depth.

OUTPUT
- Photorealistic interior photograph, shot almost straight on at eye level with a shallow depth of field.
- No text, no watermark, no people and no other wall art.`;

/**
 * Appended to the mockup prompt when the reference image is a passepartout
 * mount rather than the bare artwork.
 *
 * Without it the white border reads as empty padding and the model crops it
 * away, producing a mockup identical to the plain one - which would defeat the
 * point of a separate image for the passepartout variants.
 */
export const PASSEPARTOUT_MOCKUP_PROMPT_SUFFIX = `

PASSEPARTOUT
- The supplied image already includes a white passepartout mount around the artwork. Reproduce the whole image, mount included.
- Do not crop, trim, narrow or recolor that white border. It is part of the product being sold, not padding.`;

/**
 * Shopify's own taxonomy ids for the categories these products almost always
 * belong to, used when a store has no previous batch to carry forward from.
 *
 * Taken from Shopify's published taxonomy; the ids are stable strings, so they
 * are safe to hard-code. The full path is kept in the name so the picker shows
 * the same text the search would return.
 */
export const DEFAULT_POSTER_CATEGORY: IArtworkGenerationCategory = {
    id: 'gid://shopify/TaxonomyCategory/hg-3-4-2-1',
    name: 'Home & Garden > Decor > Artwork > Posters, Prints, & Visual Artwork > Posters'
};

export const DEFAULT_CANVAS_CATEGORY: IArtworkGenerationCategory = {
    id: 'gid://shopify/TaxonomyCategory/ae-2-1-2-14-1-2-1',
    name: 'Arts & Entertainment > Hobbies & Creative Arts > Arts & Crafts > Art & Crafting Materials > Textiles > Crafting Canvas > Painting Canvas > Canvas Panels'
};

/** Product vendor used until a batch has been submitted with one. */
export const DEFAULT_SHOPIFY_VENDOR = 'ACE';

/** Statuses an artwork can no longer move on from without manual intervention. */
export const TERMINAL_ARTWORK_STATUSES: ArtworkToGenerateStatus[] = ['completed', 'approved', 'rejected', 'failed'];

/** OpenAI batch statuses that mean the batch is done, one way or another. */
export const TERMINAL_OPENAI_BATCH_STATUSES: string[] = ['completed', 'failed', 'expired', 'cancelled'];

export const MAX_ARTWORK_ATTEMPTS = 3;

/**
 * How long a synchronously generated artwork may sit in 'generating' before the
 * worker assumes the run that owned it died and re-queues it.
 */
export const ABANDONED_ARTWORK_MINUTES = 30;
